// conversationService.ts
import { interceptorService } from "@/app/core/interceptorService";

export interface ConversationPayload {
    agent_id: string;
    agentId: string;
    title?: string;
    initialMessage?: string;
}

export interface ConversationResponse {
    conversationId: string;
    userId: string;
    agentId: string;
    title: string;
    createdAt: string;
    updatedAt: string;
    messageCount: number;
}

export interface MessagePayload {
    role: "user" | "assistant" | "system";
    content: string;
    metadata?: Record<string, any>;
}

export interface MessageResponse {
    messageId: string;
    conversationId: string;
    role: "user" | "assistant" | "system";
    content: string;
    createdAt: string;
    metadata?: Record<string, any>;
}

export interface ConversationWithMessages extends ConversationResponse {
    messages: MessageResponse[];
}


export const conversationService = {
    
    /**
     * List all conversations for the authenticated user
     */
    listConversations: async (limit: number = 50, offset: number = 0): Promise<ConversationResponse[]> => {
        return interceptorService.get(`/api/conversations?limit=${limit}&offset=${offset}`);
        // return conversations;
    },

    /**
     * Create a new conversation with an agent
     */
    createConversation: async (payload: ConversationPayload): Promise<ConversationResponse> => {
          return interceptorService.post("/api/conversations", payload)
    },

    /**
     * Get a specific conversation with all its messages
     */
    getConversation: async (conversationId: string): Promise<ConversationWithMessages> => {
         return interceptorService.get(`/api/conversations/${conversationId}`);
    },

    /**
     * Update conversation title
     */
    updateConversation: async (
        conversationId: string,
        payload: { title: string }
    ): Promise<ConversationResponse> => {
        return interceptorService.put(`/api/conversations/${conversationId}`, payload);
    },

    /**
     * Delete a conversation
     */
    deleteConversation: async (conversationId: string): Promise<void> => {
        return interceptorService.delete(`/api/conversations/${conversationId}`);
    },

    /**
     * Add a message to a conversation
     */
    addMessage: async (
        conversationId: string,
        payload: MessagePayload
    ): Promise<MessageResponse> => {
        return interceptorService.post(`/api/conversations/${conversationId}/messages`, payload);
    },

    /**
     * Get all messages in a conversation
     */
    getMessages: async (
        conversationId: string,
        limit: number = 100,
        offset: number = 0
    ): Promise<MessageResponse[]> => {
        return interceptorService.get(`/api/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`);
    },

    /**
     * Fetch conversations for a specific agent (filtered from all conversations)
     * Note: This is a client-side filter since the API returns all user conversations
     */
    fetchConversationsByAgent: async (agentId: string): Promise<ConversationResponse[]> => {
        const allConversations = await conversationService.listConversations();
        return allConversations.filter(conv => conv.agentId === agentId);
    },
};
